import type { ImageType } from '@vizit/api';
import { Badge, BadgeLevel, BadgeShape, BadgeSize, Button, ButtonLevel, ButtonSize } from '@vizit/atoms';
import { Menu, MenuItem, MenuPopover, MenuSection, MenuSeparator, MenuTrigger } from '@vizit/atoms';
import { Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { AlertCircleIcon, ChevronDownIcon, PlusIcon, RefreshCw04Icon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import type { Key, PropsWithChildren } from 'react';
import { Text as AriaText, Header } from 'react-aria-components';
import { type BenchmarkOption, findBenchmarkOption, getBenchmarkOptionKey } from '../benchmark-select-field';
import { BenchmarkIdentity, BenchmarkUnderMinimumIcon, getCustomBenchmarksHeader } from '../benchmark-select-field';
import { BenchmarkRowPopout } from './benchmark-row-popout';

/** Keys of the picker's fixed rows; option keys are `vizit:<retailer>` or `custom:<uuid>`, so they never collide. */
const CREATE_KEY = 'create';
const LOADING_KEY = 'loading';
const RETRY_KEY = 'retry';

export interface BenchmarkPickerProps {
  /**
   * Every benchmark the collection can be scored against, Vizit ones first, in the order to list them. The caller
   * has already narrowed custom sets to the collection's image type
   */
  options: BenchmarkOption[];
  /**
   * The selected option's key (see `getBenchmarkOptionKey`), or `null` when none is chosen yet
   */
  selectedKey: string | null;
  /**
   * Called with the picked option
   */
  onSelectionChange: (option: BenchmarkOption) => void;
  /**
   * Called from "+ New benchmark". Leave unset where creating is not offered (e.g. the Comparison), which hides the
   * row and its divider
   *
   * @optional
   */
  onCreateBenchmark?: () => void;
  /**
   * The collection's image type, named in the custom group's heading
   *
   * @optional
   */
  scope?: ImageType;
  /**
   * Whether the custom benchmarks are still loading; the group then shows a placeholder row
   *
   * @optional
   * @default false
   */
  isLoading?: boolean;
  /**
   * Whether the benchmarks failed to load; the trigger says so and the menu offers "Try again" when `onRetry` is
   * given
   *
   * @optional
   * @default false
   */
  isError?: boolean;
  /**
   * Called from the error state's "Try again"
   *
   * @optional
   */
  onRetry?: () => void;
  /**
   * Whether the trigger is disabled
   *
   * @optional
   * @default false
   */
  isDisabled?: boolean;
  /**
   * Whether the menu starts open
   *
   * @optional
   * @default false
   */
  defaultOpen?: boolean;
  /**
   * Additional CSS classes for the trigger button
   *
   * @optional
   */
  className?: string;
}

function SectionHeader({ children }: PropsWithChildren) {
  return (
    <Header className="px-8 pt-2 pb-3">
      <Text size={TextSize.Xs} weight={TextWeight.Medium} color={TextColor.Tertiary} className="font-mono">
        {children}
      </Text>
    </Header>
  );
}

/**
 * Picks the benchmark a collection is scored against. A standalone control, so the collection page can place it
 * and the Side-by-Side Comparison can reuse it: the trigger shows the current benchmark (logo for a Vizit one,
 * mosaic and a "Custom" badge for a custom one, "Choose benchmark" for none), and the menu lists the Vizit
 * benchmarks, then the custom ones of the collection's image type with the sets under their minimum disabled and
 * flagged, and, when `onCreateBenchmark` is given, a divider and "+ New benchmark". Hovering or focusing a custom
 * row shows its card beside it (name, image count, description, what it still needs). When the benchmarks failed to
 * load, the trigger says so and the menu offers "Try again". Controlled and fetch-free; the caller decides what
 * changing the benchmark means.
 */
export function BenchmarkPicker(props: BenchmarkPickerProps) {
  const {
    options,
    selectedKey,
    onSelectionChange,
    onCreateBenchmark,
    scope,
    isLoading = false,
    isError = false,
    onRetry,
    isDisabled = false,
    defaultOpen = false,
    className,
  } = props;

  const selected = findBenchmarkOption(options, selectedKey ?? undefined) ?? null;
  const vizitOptions = options.filter((option) => option.kind === 'vizit');
  const customOptions = options.filter((option) => option.kind === 'custom');
  const disabledKeys = [LOADING_KEY, ...options.filter((option) => option.disabled).map(getBenchmarkOptionKey)];
  // Both groups usually arrive from one route, so until anything has loaded there are no groups to head.
  const isEmptyWhileLoading = isLoading && options.length === 0;
  const loadingRow = (
    <MenuItem id={LOADING_KEY} textValue="Loading benchmarks">
      Loading benchmarks…
    </MenuItem>
  );

  // A failed read names itself on the trigger; the menu then offers "Try again" and still "New benchmark".
  const triggerLabel = isError ? "Benchmarks couldn't load" : (selected?.name ?? 'Choose benchmark');
  const triggerIcon = isError ? (
    <AlertCircleIcon size={14} className="shrink-0 text-error-600" aria-hidden />
  ) : selected ? (
    <BenchmarkIdentity option={selected} className="h-10 w-10" />
  ) : undefined;

  const handleAction = (key: Key) => {
    if (key === RETRY_KEY) {
      onRetry?.();
      return;
    }
    if (key === CREATE_KEY) {
      onCreateBenchmark?.();
      return;
    }
    const option = findBenchmarkOption(options, String(key));
    if (option) {
      onSelectionChange(option);
    }
  };

  const renderRow = (option: BenchmarkOption) => {
    const key = getBenchmarkOptionKey(option);
    return (
      <MenuItem
        key={key}
        id={key}
        textValue={option.name}
        // A disabled set still answers the pointer, so the cursor says it cannot be picked.
        className={
          option.disabled ? 'data-[disabled]:pointer-events-auto data-[disabled]:cursor-not-allowed' : undefined
        }
      >
        {({ isFocusVisible, isHovered }) => (
          <>
            <BenchmarkIdentity option={option} className="h-12 w-12" />
            {/* The name labels the row; a custom row also carries its card's facts as its description. */}
            <AriaText slot="label" className="truncate pr-10">
              {option.name}
            </AriaText>
            {option.disabled && <BenchmarkUnderMinimumIcon option={option} size={14} className="ml-auto" />}
            {/* Keyboard focus only: opening the menu focuses the selected row, which should not open its card. */}
            {option.kind === 'custom' && <BenchmarkRowPopout option={option} isActive={isFocusVisible || isHovered} />}
          </>
        )}
      </MenuItem>
    );
  };

  return (
    <MenuTrigger defaultOpen={defaultOpen}>
      <Button
        aria-label={`Benchmark: ${triggerLabel}`}
        level={ButtonLevel.Secondary}
        size={ButtonSize.Sm}
        iconStart={triggerIcon}
        iconEnd={<ChevronDownIcon size={14} aria-hidden />}
        isDisabled={isDisabled}
        className={cx('max-w-[320px]', className)}
      >
        <span className="truncate">{triggerLabel}</span>
        {!isError && selected?.kind === 'custom' && (
          <Badge level={BadgeLevel.Dark} shape={BadgeShape.Box} size={BadgeSize.Sm} bordered={false}>
            Custom
          </Badge>
        )}
      </Button>
      <MenuPopover placement="bottom start">
        <Menu
          aria-label="Benchmarks"
          selectionMode="single"
          selectedKeys={selectedKey ? [selectedKey] : []}
          disabledKeys={disabledKeys}
          onAction={handleAction}
          className="min-w-[280px] max-w-[360px]"
        >
          {isEmptyWhileLoading && <MenuSection>{loadingRow}</MenuSection>}
          {isError && onRetry && (
            <MenuSection selectionMode="none">
              <MenuItem id={RETRY_KEY} textValue="Try again">
                <RefreshCw04Icon size={14} aria-hidden />
                Try again
              </MenuItem>
            </MenuSection>
          )}
          {vizitOptions.length > 0 && (
            <MenuSection>
              <SectionHeader>VIZIT BENCHMARKS</SectionHeader>
              {vizitOptions.map(renderRow)}
            </MenuSection>
          )}
          {!isEmptyWhileLoading && (customOptions.length > 0 || isLoading) && (
            <>
              {vizitOptions.length > 0 && <MenuSeparator />}
              <MenuSection>
                <SectionHeader>{getCustomBenchmarksHeader(scope)}</SectionHeader>
                {customOptions.map(renderRow)}
                {isLoading && customOptions.length === 0 && loadingRow}
              </MenuSection>
            </>
          )}
          {onCreateBenchmark && (
            <>
              <MenuSeparator />
              {/* A command, not a choice: its own unselectable section makes it a plain menu item for assistive tech. */}
              <MenuSection selectionMode="none">
                <MenuItem id={CREATE_KEY} textValue="New benchmark">
                  <PlusIcon size={14} aria-hidden />
                  New benchmark
                </MenuItem>
              </MenuSection>
            </>
          )}
        </Menu>
      </MenuPopover>
    </MenuTrigger>
  );
}
